import {
  IconButton,
  Icons,
  LabelChipV2,
  PageHeader,
  Stack,
} from '@cast/design-system';
import {
  ClusterReconcileInfo,
  ClusterStatusChip,
  K8sProviderIconStatus,
} from '@cast/features-cluster-ui';
import {
  formatProviderNamespaceChipLabel,
  useClusterDashboardState,
} from '@cast/features-cluster-shared';

import { clusterDashboardChipSx } from '../_constants/dashboardTokens';

type ClusterDashboardHeaderProps = {
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
};

export const ClusterDashboardHeader = ({
  onRefresh,
  isRefreshing = false,
}: ClusterDashboardHeaderProps) => {
  const {
    cluster,
    isManaged,
    isKentManaged,
    showProviderNamespace,
    showReconcileInfo,
  } = useClusterDashboardState();

  const providerNamespaceLabel = formatProviderNamespaceChipLabel({
    providerType: cluster.providerType,
    providerNamespaceId: cluster.providerNamespaceId,
  });

  return (
    <PageHeader
      heading={cluster.name || 'Dashboard'}
      suffix={<K8sProviderIconStatus cluster={cluster} size={24} />}
      metadata={
        <Stack direction="row" alignItems="center" gap={8} flexWrap="wrap">
          <ClusterStatusChip
            status={cluster.displayStatus}
            managed={isManaged || isKentManaged}
            size="medium"
          />
          {showProviderNamespace && providerNamespaceLabel ? (
            <LabelChipV2
              label={providerNamespaceLabel}
              size="m"
              sx={clusterDashboardChipSx.projectName}
              testId="provider-namespace-label"
            />
          ) : null}
        </Stack>
      }
      endContent={
        <Stack direction="row" alignItems="center" gap={12}>
          {showReconcileInfo ? (
            <ClusterReconcileInfo cluster={cluster} />
          ) : null}
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isRefreshing || !onRefresh}
            loading={isRefreshing}
            aria-label="Refresh dashboard"
            testId="cluster-dashboard-refresh"
          >
            <Icons.ArrowClockwise size={20} />
          </IconButton>
        </Stack>
      }
      testId="cluster-dashboard-header"
    />
  );
};
